import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/Button";
import { NativeSelectField } from "@/components/ui/NativeSelect";
import { TextInput } from "@/components/ui/Field";
import {
  COVER_PRINT_LOG_CHANGED_EVENT,
  coverPrintActor,
  coverPrintKindLabel,
  coverPrintPlace,
  getCoverPrintLog,
  latestCoverPrint,
  logCoverPrint,
  type CoverPrintLogEntry,
} from "@/lib/coverPrintLog";
import { formatDateTime } from "@/lib/format";
import {
  getCoverPrintUrl,
  getDefaultPrintLocation,
  getPrintLocations,
  runPrintByLocationWithFeedback,
  setDefaultPrintLocation,
  type PrintLocation,
} from "@/lib/projects";
import { PrintJobFeedbackError } from "@/lib/printJob";

function locationLabel(loc: PrintLocation, fallback: number | string): string {
  return loc.name || loc.label || String(loc.id ?? loc.key ?? loc.value ?? fallback);
}

function locationKey(loc: PrintLocation, idx: number): string {
  const id = loc.id ?? loc.key ?? loc.value ?? idx;
  return String(id);
}

export function CoverPrintSection({
  projectId,
  className = "",
  hideHeading = false,
}: {
  projectId: number;
  className?: string;
  hideHeading?: boolean;
}) {
  const [locations, setLocations] = useState<PrintLocation[]>([]);
  const [locationId, setLocationId] = useState("");
  const [copies, setCopies] = useState("1");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [entries, setEntries] = useState<CoverPrintLogEntry[] | null>(null);
  const [showAll, setShowAll] = useState(false);

  const reloadLog = useCallback(async () => {
    try {
      const list = await getCoverPrintLog(projectId);
      setEntries(Array.isArray(list) ? list : []);
    } catch {
      setEntries([]);
    }
  }, [projectId]);

  useEffect(() => {
    void (async () => {
      try {
        const [locs, def] = await Promise.all([getPrintLocations(), getDefaultPrintLocation()]);
        setLocations(Array.isArray(locs) ? locs : []);
        if (def?.default_location_id) {
          setLocationId(String(def.default_location_id));
        } else if (locs?.[0]) {
          const first = locs[0];
          const id = first.id ?? first.key ?? first.value;
          if (id != null) setLocationId(String(id));
        }
      } catch {
        setLocations([]);
      }
    })();
  }, []);

  useEffect(() => {
    void reloadLog();
  }, [reloadLog]);

  useEffect(() => {
    function onLogged() {
      void reloadLog();
    }
    window.addEventListener(COVER_PRINT_LOG_CHANGED_EVENT, onLogged);
    return () => window.removeEventListener(COVER_PRINT_LOG_CHANGED_EVENT, onLogged);
  }, [reloadLog]);

  async function handlePrint() {
    const loc = Number(locationId);
    const numCopies = Math.max(1, Number(copies) || 1);
    if (!loc) {
      setMessage("Bitte Standort wählen.");
      return;
    }
    setBusy(true);
    setMessage("");
    try {
      await setDefaultPrintLocation(loc);
      const { url } = await getCoverPrintUrl(projectId);
      const job = await runPrintByLocationWithFeedback(loc, numCopies, url);
      const selected = locations.find((item) => Number(item.id ?? item.key ?? item.value) === loc);
      await logCoverPrint(
        projectId,
        {
          cover_kind: "project",
          location_id: loc,
          location_name: selected ? locationLabel(selected, loc) : null,
          copies: numCopies,
        },
        job,
      );
      await reloadLog();
      setMessage("Druck erfolgreich.");
    } catch (err) {
      if (err instanceof PrintJobFeedbackError) {
        setMessage(err.feedback.message || err.feedback.title);
      } else {
        setMessage(err instanceof Error ? err.message : "Druck fehlgeschlagen.");
      }
    } finally {
      setBusy(false);
    }
  }

  const lastProject = latestCoverPrint(entries, "project");
  const lastAny = latestCoverPrint(entries);
  const visible = showAll ? entries ?? [] : (entries ?? []).slice(0, 3);
  const hiddenCount = Math.max(0, (entries?.length ?? 0) - visible.length);

  return (
    <div className={className}>
      {hideHeading ? null : (
        <h4 className="mb-2 text-xs font-bold uppercase tracking-wide text-label-secondary">
          Deckblatt drucken
        </h4>
      )}

      {entries !== null ? (
        <div className={lastAny ? "eh-po-cover-log eh-po-cover-log--ok" : "eh-po-cover-log"}>
          {lastProject ? (
            <>
              <p className="eh-po-cover-log-title">Zuletzt gedruckt</p>
              <p className="eh-po-cover-log-line">
                {formatDateTime(lastProject.created_at)} · {coverPrintActor(lastProject)} ·{" "}
                {coverPrintPlace(lastProject)}
                {lastProject.copies && lastProject.copies > 1
                  ? ` · ${lastProject.copies} Kopien`
                  : ""}
              </p>
            </>
          ) : (
            <p className="eh-po-cover-log-title">Noch kein Projekt-Deckblatt gedruckt</p>
          )}
        </div>
      ) : null}

      <div className="eh-po-print-grid">
        <NativeSelectField
          label="Standort"
          selectSize="sm"
          value={locationId}
          onChange={(e) => setLocationId(e.target.value)}
        >
          {locations.length === 0 ? (
            <option value="">— Standort auswählen —</option>
          ) : null}
          {locations.map((loc, idx) => (
            <option key={locationKey(loc, idx)} value={locationKey(loc, idx)}>
              {locationLabel(loc, idx)}
            </option>
          ))}
        </NativeSelectField>
        <TextInput
          label="Kopien"
          type="number"
          min={1}
          inputClassName="!w-full"
          className="mb-0"
          value={copies}
          onChange={(e) => setCopies(e.target.value)}
        />
        <Button type="button" size="sm" disabled={busy || !locationId} onClick={() => void handlePrint()}>
          Drucken
        </Button>
      </div>
      {message ? <p className="mt-1 text-xs text-label-tertiary">{message}</p> : null}

      {visible.length > 0 ? (
        <div className="eh-po-cover-history">
          <h5 className="eh-po-cover-history-title">Druckhistorie</h5>
          <ul>
            {visible.map((entry) => (
              <li key={entry.id} className="eh-po-cover-history-item">
                <span className="eh-po-cover-history-kind">
                  {entry.cover_kind_label || coverPrintKindLabel(entry.cover_kind)}
                </span>
                <span className="eh-po-cover-history-meta">
                  {formatDateTime(entry.created_at)} · {coverPrintActor(entry)} · {coverPrintPlace(entry)}
                </span>
              </li>
            ))}
          </ul>
          {hiddenCount > 0 && !showAll ? (
            <button type="button" className="eh-po-cover-history-more" onClick={() => setShowAll(true)}>
              {hiddenCount} weitere anzeigen
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
